import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Languages, AlertTriangle, FileSearch, CheckCircle2, Globe } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { toast } from 'sonner';

/**
 * /admin/i18n-coverage — bilingual platform health.
 *
 * Reads the static EN/ES locale JSONs and a heuristic hard-coded-string
 * scan from GET /api/admin/i18n/coverage. Helps prevent silent ES regressions.
 */
const toneFor = (pct) =>
  pct >= 100 ? { tag: 'success', text: 'text-success', bg: 'bg-success/10', border: 'border-success/30' }
  : pct >= 95  ? { tag: 'info',    text: 'text-info',    bg: 'bg-info/10',    border: 'border-info/30' }
  : pct >= 85  ? { tag: 'warning', text: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' }
  :              { tag: 'destructive', text: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30' };

export default function AdminI18nCoverage() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/i18n/coverage');
      setData(data);
    } catch (e) {
      toast.error(isES ? 'Error al cargar el informe' : 'Failed to load report', { description: formatApiError(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cov = data?.coverage;
  const overallTone = useMemo(() => toneFor(cov?.overall_pct ?? 0), [cov]);

  const filteredNsRaw = useMemo(() => {
    if (!cov?.namespaces) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return cov.namespaces;
    return cov.namespaces.filter((n) =>
      n.namespace.toLowerCase().includes(needle)
      || n.missing_in_es.some(k => k.toLowerCase().includes(needle))
      || n.missing_in_en.some(k => k.toLowerCase().includes(needle)),
    );
  }, [cov, q]);

  const nsSort = useSort();
  const filteredNs = useMemo(() => nsSort.apply(filteredNsRaw, {
    namespace: (n) => n.namespace,
    coverage:  (n) => n.coverage_pct,
    en:        (n) => n.en_keys,
    es:        (n) => n.es_keys,
    missingEs: (n) => n.missing_in_es.length,
    missingEn: (n) => n.missing_in_en.length,
  }), [filteredNsRaw, nsSort]);

  const hcSort = useSort();
  const filteredHCRaw = useMemo(() => {
    const list = data?.hardcoded || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((h) =>
      h.file.toLowerCase().includes(needle)
      || h.phrase.toLowerCase().includes(needle),
    );
  }, [data, q]);
  const filteredHC = useMemo(() => hcSort.apply(filteredHCRaw, {
    file:   (h) => h.file,
    line:   (h) => h.line,
    kind:   (h) => h.kind,
    phrase: (h) => h.phrase,
  }), [filteredHCRaw, hcSort]);

  return (
    <>
      <Topbar
        title={isES ? 'Salud bilingüe de la plataforma' : 'Bilingual platform health'}
        subtitle={isES ? 'Cobertura ES/EN, claves faltantes y cadenas no traducidas' : 'ES/EN coverage, missing keys, and untranslated strings'}
        actions={
          <Button size="sm" variant="outline" onClick={load} data-testid="i18n-refresh">
            <Globe className="h-4 w-4 mr-1.5" /> {isES ? 'Recargar' : 'Refresh'}
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-5" data-testid="i18n-coverage-page">
        {loading && (
          <Card className="p-10 shadow-card flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {isES ? 'Cargando…' : 'Loading…'}
          </Card>
        )}

        {!loading && cov && (
          <>
            {/* Overall coverage strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className={`p-4 shadow-card ${overallTone.bg} border ${overallTone.border}`} data-testid="i18n-overall">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{isES ? 'Cobertura total' : 'Overall coverage'}</p>
                <p className={`text-3xl font-display font-bold mt-1 tabular-nums ${overallTone.text}`}>{cov.overall_pct}%</p>
                <p className="text-xs text-muted-foreground mt-1">{cov.total_keys} {isES ? 'claves totales' : 'total keys'}</p>
              </Card>
              <Card className="p-4 shadow-card">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">EN</p>
                <p className="text-2xl font-display font-bold mt-1 tabular-nums">{cov.en_keys}</p>
                <p className="text-xs text-muted-foreground mt-1">{isES ? 'claves' : 'keys'}</p>
              </Card>
              <Card className="p-4 shadow-card">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">ES</p>
                <p className="text-2xl font-display font-bold mt-1 tabular-nums">{cov.es_keys}</p>
                <p className="text-xs text-muted-foreground mt-1">{isES ? 'claves' : 'keys'}</p>
              </Card>
              <Card className="p-4 shadow-card border-amber-200/60 bg-amber-50/30" data-testid="i18n-missing-totals">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{isES ? 'Faltantes' : 'Missing'}</p>
                <div className="flex items-baseline gap-3 mt-1">
                  <div><span className="text-xs">ES:</span> <span className="text-xl font-display font-bold tabular-nums">{cov.missing_in_es_count}</span></div>
                  <div><span className="text-xs">EN:</span> <span className="text-xl font-display font-bold tabular-nums">{cov.missing_in_en_count}</span></div>
                </div>
              </Card>
            </div>

            {/* Search */}
            <Card className="p-3 shadow-card flex items-center gap-3">
              <FileSearch className="h-4 w-4 text-muted-foreground ml-2" />
              <Input
                placeholder={isES ? 'Filtrar por namespace, clave, archivo…' : 'Filter by namespace, key, file…'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="border-0 focus-visible:ring-0"
                data-testid="i18n-filter"
              />
            </Card>

            <Tabs defaultValue="coverage">
              <TabsList>
                <TabsTrigger value="coverage" data-testid="tab-coverage">
                  <Languages className="h-4 w-4 mr-1.5" /> {isES ? 'Cobertura por sección' : 'Coverage by namespace'}
                </TabsTrigger>
                <TabsTrigger value="hardcoded" data-testid="tab-hardcoded">
                  <AlertTriangle className="h-4 w-4 mr-1.5" /> {isES ? 'Cadenas sospechosas' : 'Suspected hard-coded'} ({data.hardcoded.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="coverage" className="mt-4">
                <Card className="shadow-card overflow-hidden" data-testid="ns-table">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-5 py-3"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="namespace">{isES ? 'Namespace' : 'Namespace'}</SortableHeader></th>
                        <th className="px-5 py-3 text-right"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="coverage">{isES ? 'Cobertura' : 'Coverage'}</SortableHeader></th>
                        <th className="px-5 py-3 text-right"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="en">EN</SortableHeader></th>
                        <th className="px-5 py-3 text-right"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="es">ES</SortableHeader></th>
                        <th className="px-5 py-3 text-right"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="missingEs">{isES ? 'Faltan ES' : 'Missing in ES'}</SortableHeader></th>
                        <th className="px-5 py-3 text-right"><SortableHeader sort={nsSort} testIdPrefix="i18ncov-ns" k="missingEn">{isES ? 'Faltan EN' : 'Missing in EN'}</SortableHeader></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredNs.map((n) => {
                        const tone = toneFor(n.coverage_pct);
                        return (
                          <tr key={n.namespace} className="hover:bg-muted/20" data-testid={`ns-row-${n.namespace}`}>
                            <td className="px-5 py-3 font-medium">{n.namespace}</td>
                            <td className="px-5 py-3 text-right">
                              <Badge variant="outline" className={`${tone.text} ${tone.border}`}>
                                {n.coverage_pct === 100 && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {n.coverage_pct}%
                              </Badge>
                            </td>
                            <td className="px-5 py-3 text-right tabular-nums">{n.en_keys}</td>
                            <td className="px-5 py-3 text-right tabular-nums">{n.es_keys}</td>
                            <td className="px-5 py-3 text-right tabular-nums text-amber-700">{n.missing_in_es.length}</td>
                            <td className="px-5 py-3 text-right tabular-nums text-amber-700">{n.missing_in_en.length}</td>
                          </tr>
                        );
                      })}
                      {!filteredNs.length && (
                        <tr><td colSpan={6} className="px-5 py-6 text-center text-muted-foreground italic">{isES ? 'Sin resultados.' : 'No results.'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </Card>

                {(cov.missing_in_es.length > 0 || cov.missing_in_en.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {cov.missing_in_es.length > 0 && (
                      <Card className="p-4 shadow-card border-amber-200/60" data-testid="missing-in-es">
                        <h4 className="text-sm font-semibold mb-2">{isES ? 'Claves faltantes en ES' : 'Keys missing in ES'} <span className="text-muted-foreground">({cov.missing_in_es.length})</span></h4>
                        <ul className="text-xs space-y-0.5 max-h-72 overflow-y-auto font-mono">
                          {cov.missing_in_es.slice(0, 60).map((k) => <li key={k} className="truncate text-muted-foreground">{k}</li>)}
                        </ul>
                      </Card>
                    )}
                    {cov.missing_in_en.length > 0 && (
                      <Card className="p-4 shadow-card border-amber-200/60" data-testid="missing-in-en">
                        <h4 className="text-sm font-semibold mb-2">{isES ? 'Claves faltantes en EN' : 'Keys missing in EN'} <span className="text-muted-foreground">({cov.missing_in_en.length})</span></h4>
                        <ul className="text-xs space-y-0.5 max-h-72 overflow-y-auto font-mono">
                          {cov.missing_in_en.slice(0, 60).map((k) => <li key={k} className="truncate text-muted-foreground">{k}</li>)}
                        </ul>
                      </Card>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="hardcoded" className="mt-4">
                <Card className="shadow-card overflow-hidden" data-testid="hc-table">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-5 py-3"><SortableHeader sort={hcSort} testIdPrefix="i18ncov-hc" k="file">{isES ? 'Archivo' : 'File'}</SortableHeader></th>
                        <th className="px-5 py-3 w-16"><SortableHeader sort={hcSort} testIdPrefix="i18ncov-hc" k="line">{isES ? 'Línea' : 'Line'}</SortableHeader></th>
                        <th className="px-5 py-3"><SortableHeader sort={hcSort} testIdPrefix="i18ncov-hc" k="phrase">{isES ? 'Cadena' : 'Phrase'}</SortableHeader></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredHC.map((h, idx) => (
                        <tr key={`${h.file}-${h.line}-${idx}`} className="hover:bg-muted/20" data-testid="hc-row">
                          <td className="px-5 py-3 font-mono text-xs">{h.file}</td>
                          <td className="px-5 py-3 text-xs text-muted-foreground tabular-nums">{h.line}</td>
                          <td className="px-5 py-3">"{h.phrase}"</td>
                        </tr>
                      ))}
                      {!filteredHC.length && (
                        <tr><td colSpan={3} className="px-5 py-6 text-center text-muted-foreground italic">
                          {data.hardcoded.length === 0
                            ? (isES ? '✓ Sin cadenas codificadas detectadas' : '✓ No hard-coded strings detected')
                            : (isES ? 'Sin coincidencias.' : 'No matches.')}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </Card>
                <p className="text-xs text-muted-foreground mt-3 italic">
                  {isES
                    ? 'Detección heurística — revise cada resultado antes de envolverlo en t(...). Los nombres propios (TalentMap, Corporate Talent Hub, etc.) se omiten automáticamente.'
                    : 'Heuristic detection — review each match before wrapping it in t(...). Brand names (TalentMap, Corporate Talent Hub, etc.) are auto-skipped.'}
                </p>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </>
  );
}
